import { REGION_CITY_ROWS } from '../data/regionsCatalog';
import { getCityNativeLabel } from '../data/onboardingRegionLabels';

/** Canonical default — matches public.locations.code */
export const DEFAULT_CITY_CODE = 'danang';

/** @deprecated use DEFAULT_CITY_CODE */
export const DEFAULT_REGION_CODE = DEFAULT_CITY_CODE;

/** @type {Record<string, string>} Legacy IATA → locations.code */
const IATA_TO_LOCATION = {
  DAD: 'danang',
  SGN: 'hochiminh',
  HAN: 'hanoi',
  HUI: 'hoian',
  HOI: 'hoian',
  HUE: 'hue',
};

/** @type {Record<string, string>} Legacy slug / alias → locations.code */
const LEGACY_ALIAS_TO_LOCATION = {
  da_nang: 'danang',
  'da-nang': 'danang',
  dad: 'danang',
  hcmc: 'hochiminh',
  'ho-chi-minh': 'hochiminh',
  saigon: 'hochiminh',
  hoi_an: 'hoian',
  'hoi-an': 'hoian',
  hui: 'hoian',
};

function normRegionLabel(s) {
  return String(s || '')
    .trim()
    .normalize('NFC')
    .toLowerCase()
    .replace(/\(gps\)/gi, '')
    .replace(/\s+/g, ' ');
}

function stripDiacritics(s) {
  return String(s || '')
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D');
}

function resolveCatalogKey(label) {
  const raw = normRegionLabel(label);
  if (!raw) return null;
  const rawAscii = stripDiacritics(raw);

  for (const row of REGION_CITY_ROWS) {
    if (!row.key) continue;
    const names = [row.pick, row.ko, getCityNativeLabel(row)].map(normRegionLabel).filter(Boolean);
    for (const name of names) {
      if (name === raw || stripDiacritics(name) === rawAscii) {
        return row.key;
      }
    }
  }
  return null;
}

/** @type {Record<string, string[]>} Canonical slug → DB region values (incl. legacy IATA/underscore) */
const LOCATION_LEGACY_REGION_VALUES = {
  danang: ['danang', 'DAD', 'da_nang', 'da-nang', 'dad'],
  hochiminh: ['hochiminh', 'SGN', 'hcmc', 'saigon', 'ho-chi-minh'],
  hanoi: ['hanoi', 'HAN'],
  hoian: ['hoian', 'HUI', 'HOI', 'hoi_an', 'hoi-an', 'hui'],
  hue: ['hue', 'HUE'],
};

/**
 * DB region column filter values — canonical slug + legacy aliases (read/query only).
 * @param {string | null | undefined} cityCode
 * @returns {string[]}
 */
export function regionFilterValues(cityCode) {
  const slug = normalizeCityCode(cityCode);
  if (!slug) return [];
  const preset = LOCATION_LEGACY_REGION_VALUES[slug];
  if (preset) return [...new Set(preset)];
  return [slug];
}

/**
 * Normalize any stored/input region token to canonical locations.code (lowercase slug).
 * Returns empty string when input is empty.
 * @param {string | null | undefined} input
 * @returns {string}
 */
export function normalizeCityCode(input) {
  const raw = normRegionLabel(input);
  if (!raw) return '';

  const upper = String(input || '').trim().toUpperCase();
  if (IATA_TO_LOCATION[upper]) return IATA_TO_LOCATION[upper];
  if (LEGACY_ALIAS_TO_LOCATION[raw]) return LEGACY_ALIAS_TO_LOCATION[raw];

  for (const row of REGION_CITY_ROWS) {
    if (row.key && row.key.toLowerCase() === raw) return row.key;
  }

  // "Vietnam · Da Nang" / "Вьетнам · Дананг" — try each segment
  if (/[·,]/.test(raw)) {
    const parts = raw.split(/[·,]/).map((p) => p.trim()).filter(Boolean);
    for (const part of parts) {
      const nested = normalizeCityCode(part);
      if (nested && nested !== part && /^[a-z][a-z0-9_]*$/i.test(nested)) return nested;
      for (const row of REGION_CITY_ROWS) {
        if (row.key && row.key.toLowerCase() === nested) return row.key;
      }
    }
  }

  const ascii = stripDiacritics(raw);
  if (
    ascii.includes('danang')
    || ascii.includes('da nang')
    || raw.includes('다낭')
    || raw.includes('дананг')
  ) {
    return 'danang';
  }
  if (
    ascii.includes('hoian')
    || ascii.includes('hoi an')
    || raw.includes('호이안')
    || raw.includes('хойан')
  ) {
    return 'hoian';
  }
  if (ascii.includes('hue') || raw.includes('후에') || raw.includes('huế') || raw.includes('хюэ') || raw.includes('хюе')) {
    return 'hue';
  }
  if (ascii.includes('hanoi') || ascii.includes('ha noi') || raw.includes('하노이') || raw.includes('ханой')) {
    return 'hanoi';
  }
  if (
    ascii.includes('ho chi')
    || ascii.includes('saigon')
    || raw.includes('호치민')
    || raw.includes('хошимин')
    || raw.includes('сайгон')
  ) {
    return 'hochiminh';
  }
  if (ascii.includes('nha trang') || raw.includes('나트랑') || raw.includes('нячанг')) return 'nhatrang';
  if (ascii.includes('da lat') || raw.includes('달랏') || raw.includes('далат')) return 'dalat';

  const fromCatalog = resolveCatalogKey(input);
  if (fromCatalog) return fromCatalog;

  // Reject non-slug leftovers (e.g. Cyrillic labels) — callers fall back to DEFAULT.
  if (!/^[a-z][a-z0-9_]*$/i.test(raw)) return '';
  return raw;
}

/**
 * Label/slug/IATA → locations.code with default fallback.
 * @param {string} [regionSlugOrLabel]
 * @returns {string}
 */
export function resolveLocationCityCode(regionSlugOrLabel) {
  const code = normalizeCityCode(regionSlugOrLabel);
  return code || DEFAULT_CITY_CODE;
}

/** @deprecated use resolveLocationCityCode */
export const resolveDiscoverRegionCode = resolveLocationCityCode;
